import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { BookOpen, Leaf, Users, Mail, MessageCircle, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, WELCOME_TEXT, ERROR_TEXT } from "@/lib/api";
import { Skeleton } from "@/components/hm/Basics";
import { NextStep, PlanTile, FreeResources } from "@/screens/dashboard/Tiles";
import { JournalTile } from "@/screens/dashboard/JournalTile";

const SPACES = [
  { to: "/academia", icon: BookOpen, title: "Academia", id: "academia" },
  { to: "/recursos", icon: Leaf, title: "Recursos", id: "recursos" },
  { to: "/comunidad", icon: Users, title: "Comunidad", id: "comunidad" },
];

const WelcomeTile = ({ user, onClose }) => (
  <div className="welcome-tile hm-fade-up" style={{ marginBottom: 16 }} data-testid="welcome-notices">
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <strong>Tu bienvenida</strong>
      <button onClick={onClose} className="icon-btn" style={{ width: 32, height: 32 }} aria-label="Cerrar" data-testid="welcome-close"><X /></button>
    </div>
    {user.welcome_email_status !== "failed" && (
      <p style={{ margin: 0, fontSize: 14 }} className="inline-flex items-center gap-2" data-testid="welcome-email-status">
        <Mail size={15} className="text-accent" /> Te hemos enviado un mail de bienvenida a {user.email}.
      </p>
    )}
    <p style={{ margin: 0, fontSize: 13 }} className="text-muted inline-flex items-center gap-2"><MessageCircle size={15} className="text-accent" /> Aviso de WhatsApp preparado</p>
    <p className="wa-bubble" style={{ margin: 0 }} data-testid="whatsapp-preview">
      Hola {user.name}. Soy Heartmony. Ya tienes tu espacio listo. Empieza por Academia, en tu primer módulo. Cuando quieras, comparte tu avance en la comunidad.
    </p>
  </div>
);

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const [modules, setModules] = useState(null);
  const [error, setError] = useState(false);
  const [welcome, setWelcome] = useState(() => !!sessionStorage.getItem("hm_welcome"));
  const closeWelcome = () => { sessionStorage.removeItem("hm_welcome"); setWelcome(false); };

  useEffect(() => { api.get("/modules").then((r) => setModules(r.data)).catch(() => setError(true)); }, []);
  useEffect(() => { if (params.get("plan")) api.post("/billing/sync").then(refresh).catch(() => {}); }, [params, refresh]);
  useEffect(() => {
    if (hash === "#diario" && modules) setTimeout(() => document.getElementById("diario")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [hash, modules]);

  const today = new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div data-testid="dashboard">
      <p className="text-muted" style={{ margin: "0 0 .3rem", fontSize: 14 }}>{today.charAt(0).toUpperCase() + today.slice(1)}</p>
      <h1 className="hm-h2" style={{ margin: "0 0 .7rem", lineHeight: 1.25 }}>Hola, {user.name}.</h1>
      <p className="serif" style={{ fontSize: "1.45rem", margin: "0 0 1.8rem", lineHeight: 1.3 }} data-testid="dashboard-welcome">{WELCOME_TEXT}</p>
      {welcome && <WelcomeTile user={user} onClose={closeWelcome} />}
      {error && <p className="text-danger">{ERROR_TEXT}</p>}
      {!modules && !error && <Skeleton h={140} n={2} />}
      {modules && (
        <>
          <div className="dash" style={{ gridTemplateColumns: "1fr" }}>
            <NextStep />
          </div>
          <div className="space-links">
            {SPACES.map((s) => (
              <Link key={s.id} to={s.to} className="space-link" data-testid={`dashboard-space-${s.id}`}><s.icon size={20} />{s.title}</Link>
            ))}
          </div>
          <div className="dash-grid3 two">
            <JournalTile />
            <PlanTile />
          </div>
          <FreeResources />
        </>
      )}
    </div>
  );
}
